import { create } from "zustand";
import { persist } from "zustand/middleware";

import { createRawPersistStorage } from "@/lib/platform/clientStorage";

const STORAGE_KEY = "trovie.public-browse-view";

export type BrowseLayout = "list" | "grid";

type PublicBrowseViewStore = {
  layout: BrowseLayout | null;
  setLayout: (next: BrowseLayout) => void;
};
type PersistedPublicBrowseView = Pick<PublicBrowseViewStore, "layout">;

const usePublicBrowseViewStore = create<PublicBrowseViewStore>()(
  persist<PublicBrowseViewStore, [], [], PersistedPublicBrowseView>(
    (set) => ({ layout: null, setLayout: (layout) => set({ layout }) }),
    {
      name: STORAGE_KEY,
      storage: createRawPersistStorage<PersistedPublicBrowseView>(
        (value) => value === "list" || value === "grid" ? { layout: value } : null,
        // "" encodes "no choice yet": Zustand writes the store back once hydration settles even
        // when nothing was stored, and the decoder above reads "" as no stored choice rather than
        // as a layout.
        (state) => state.layout ?? "",
      ),
      partialize: ({ layout }) => ({ layout }),
    },
  ),
);

/**
 * The customer browse page's grid or list layout, which is the customer's own choice, not the
 * vendor's (#341). Persisted across visits with the same small Zustand store shape as
 * `useInventoryView`, but under its own storage key rather than `"trovie.inventory-view"`. This is
 * a separate, unauthenticated surface, so a staff member previewing their own event's QR while
 * signed into the vendor app must not have the two preferences mixed or overwrite each other.
 *
 * The store holds no opening layout: `layout` is `null` until the customer touches the toggle. Two
 * customer surfaces read it and open differently: the public-inventory storefront leads with card
 * art on grid, and the Pull Sheet browse page stays on list. Each passes its own opening layout, so
 * neither re-opens the other's. Once a customer chooses, that choice persists and wins on both.
 *
 * @param opensOn The layout this surface shows a customer who has never touched the toggle.
 */
export function usePublicBrowseView(
  opensOn: BrowseLayout,
): readonly [BrowseLayout, (next: BrowseLayout) => void] {
  const layout = usePublicBrowseViewStore((state) => state.layout);
  const setLayout = usePublicBrowseViewStore((state) => state.setLayout);
  return [layout ?? opensOn, setLayout] as const;
}
